import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    color: 'white',
    backgroundColor: '#252731',
    borderRadius: '8px',
    '& fieldset': { borderColor: '#343847' },
    '&:hover fieldset': { borderColor: '#343847' },
    '&.Mui-focused fieldset': { borderColor: '#3a54c0' }
  },
  '& .MuiInputBase-input::placeholder': { color: '#7a8499', opacity: 1 }
};

const primaryBtnSx = {
  textTransform: 'none',
  fontWeight: 600,
  backgroundColor: '#3a54c0',
  '&:hover': { backgroundColor: '#314aad' },
  '&.Mui-disabled': { backgroundColor: '#2a3350', color: '#7a8499' }
};

const darkBtnSx = {
  textTransform: 'none',
  fontWeight: 600,
  color: '#e7eaf2',
  backgroundColor: '#252a36',
  '&:hover': { backgroundColor: '#2c3240' }
};

/**
 * Two-step "Reset your password" dialog (web port of the desktop modal).
 *
 * Step 1 collects an email OR username and calls `onSubmit({ email })`. Step 2
 * shows a neutral confirmation that never reveals whether an account exists —
 * the backend triggers the matching membership site's native WordPress reset
 * email.
 */
export default function ForgotPasswordModal({ open, onClose, onSubmit, defaultEmail = '' }) {
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState(defaultEmail);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setStep(1);
      setEmail(defaultEmail || '');
      setError('');
      setSubmitting(false);
    }
  }, [open, defaultEmail]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    const trimmed = email.trim();
    // The bridge accepts an email OR a membership username, so don't enforce an
    // email pattern here — just require something.
    if (!trimmed) {
      setError('Please enter your email or username.');
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      const result = await onSubmit({ email: trimmed });
      // Neutral by design: advance to the confirmation step regardless of whether
      // the address maps to an account, unless the request itself failed to send.
      if (result && result.status === false) {
        setError(result.message || 'Something went wrong. Please try again.');
        return;
      }
      setStep(2);
    } catch (err) {
      setError(err?.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      PaperProps={{
        sx: {
          backgroundColor: '#181B24',
          color: 'white',
          border: '1px solid #2b3140',
          borderRadius: '12px'
        }
      }}
    >
      {step === 1 ? (
        <form noValidate onSubmit={handleSubmit}>
          <DialogTitle sx={{ fontSize: 18, fontWeight: 700, pb: 0.5 }}>Reset your password</DialogTitle>
          <DialogContent>
            <Stack spacing={2}>
              <Typography sx={{ fontSize: 14, color: '#9aa3b4' }}>
                Enter the email or username you use on your membership site.
              </Typography>
              <TextField
                autoFocus
                fullWidth
                size="small"
                placeholder="email@example.com"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                error={Boolean(error)}
                helperText={error || ' '}
                FormHelperTextProps={{ sx: { color: '#ff7a7a', ml: 0.5 } }}
                sx={fieldSx}
              />
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 1, flexDirection: 'column', alignItems: 'stretch' }}>
            <Button type="submit" fullWidth variant="contained" disableElevation disabled={submitting} sx={primaryBtnSx}>
              {submitting ? 'Sending…' : 'Send reset link'}
            </Button>
          </DialogActions>
          <Typography sx={{ fontSize: 12, color: '#7c8597', px: 3, pb: 3, pt: 1, lineHeight: '16px' }}>
            The reset email will come from the site where you purchased your membership — check spam too.
          </Typography>
        </form>
      ) : (
        <>
          <DialogContent sx={{ pt: 4 }}>
            <Stack spacing={2} alignItems="center" sx={{ textAlign: 'center' }}>
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: '50%',
                  backgroundColor: '#1f7a46',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <CheckRoundedIcon sx={{ color: '#d6ffe4', fontSize: 30 }} />
              </Box>
              <Typography sx={{ fontSize: 18, fontWeight: 700 }}>Check your inbox</Typography>
              <Typography sx={{ fontSize: 14, color: '#9aa3b4', maxWidth: 320 }}>
                If an account exists, we&apos;ve sent a password reset link. The email comes from your membership site.
              </Typography>
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Button fullWidth variant="contained" disableElevation onClick={onClose} sx={darkBtnSx}>
              Back to login
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
}
